import * as actions from '@common-modules/mediaDetails/actions';

import { WorkerState } from '@worker';
import { defaultState } from '../../store';
import { getBoundingPolyFromGap } from '@worker/helpers/BoundingPoly';
import { CaseReducer, createReducer, Draft, original } from '@reduxjs/toolkit';

type Re<P = unknown> = CaseReducer<WorkerState, { payload: P; type: string }>;

export function addGapToAutoDetectedSeries(
  state: Draft<WorkerState>,
  gapId: string,
  groupId: string,
  isDeleted?: boolean
) {
  const orig_state = original<Readonly<WorkerState>>(state);
  const gaps = orig_state?.detectionGaps[groupId];

  if (!gaps) {
    return;
  }

  const gap = gaps[gapId];
  if (!gap) {
    return;
  }

  const group = state.groupMap[groupId];
  if (!group) {
    return;
  }

  const newPoly = getBoundingPolyFromGap(gap, group, isDeleted);

  const subsegmentId = newPoly.subsegmentId;

  const newSubsegment = {
    id: subsegmentId,
    groupId,
    startTimeMs: newPoly.startTimeMs,
    stopTimeMs: newPoly.stopTimeMs,
    objectIds: [newPoly.id],
  };
  // TODO: add addGroup function and use it here

  const insertIndex = group.subsegmentIds.findIndex((currSubsegmentId) => {
    // Would prefer to use orig_state here but this function is called multiple times in a loop and may affect the index
    const currSubsegment = state.subsegmentMap[currSubsegmentId];
    if (!currSubsegment) {
      return false;
    }
    return currSubsegment.startTimeMs > newSubsegment.startTimeMs;
  });

  if (insertIndex !== -1) {
    state.subsegmentMap[subsegmentId] = newSubsegment;
    state.objectMap[newPoly.id] = newPoly;
    group.subsegmentIds.splice(insertIndex, 0, subsegmentId);
    const detectionGaps = state.detectionGaps[groupId];
    if (detectionGaps) {
      delete detectionGaps[gap.id];
    }
  }

  // return {
  //   ...state,
  //   subsegmentMap: {
  //       ...state.subsegmentMap,
  //       [subsegmentId]: newSubsegment,
  //     },
  //   objectMap: {
  //       ...state.objectMap,
  //       [newPoly.id]: newPoly,
  //   },
  //   groupMap: {
  //     ...state.groupMap,
  //       [groupId]: {
  //         ...cluster,
  //         groups: [
  //           ...cluster.subsegmentIds.slice(0, insertIndex),
  //           subsegmentId,
  //           ...cluster.subsegmentIds.slice(insertIndex),
  //         ],
  //       },
  //     },
  //   },
  //   detectionGaps: {
  //     ...state.detectionGaps,
  //     [groupId]: facesGapGroup,
  //   },
  // };
  // return state;
}

export function moveManualGaptoNewGroup(
  state: Draft<WorkerState>,
  subsegmentId: string,
  groupId: string,
  newGroupId: string
) {
  const orig_state = original<Readonly<WorkerState>>(state);

  const gaps = orig_state?.detectionGaps[groupId];
  if (!gaps) {
    return;
  }

  // need to find gap with subsegmentId
  const gap = Object.values(gaps).find(
    (gap) => gap.subsegmentId === subsegmentId
  );
  if (!gap) {
    return;
  }

  const group = state.groupMap[groupId];
  if (!group) {
    return;
  }

  const startTimeMs = gap.leftObject.stopTimeMs;
  const stopTimeMs = gap.rightObject.startTimeMs;
  const newSubsegmentId = `${startTimeMs}-${stopTimeMs}-${newGroupId}`;
  const newObjectId = newSubsegmentId; // same id is okay

  const newPoly = getBoundingPolyFromGap(
    gap,
    group,
    false,
    newObjectId,
    newGroupId,
    newGroupId,
    true
  );

  const newSubsegment = {
    id: newSubsegmentId,
    groupId: newGroupId,
    startTimeMs,
    stopTimeMs,
    objectIds: [newPoly.id],
  };

  state.objectMap[newObjectId] = newPoly;

  // preserve selected status - this isn't working because outbound saga not se does update!
  state.selectedPolyGroups[newSubsegmentId] =
    state.selectedPolyGroups[subsegmentId];

  // promote old gap to series item and mark as deleted to prevent instant regeneration
  addGapToAutoDetectedSeries(state, gap.id, groupId, true);

  return newSubsegment;
}

const onChangeOverlay: Re<{
  id: string;
  groupId: string;
  type: string;
}> = (state, { payload }) => {
  if (payload.type === 'udr') {
    return;
  }

  addGapToAutoDetectedSeries(state, payload.id, payload.groupId);
};

export default createReducer(defaultState, (builder) => {
  builder.addCase(actions.CHANGE_OVERLAY, onChangeOverlay);
});
